/* eslint-disable no-underscore-dangle */
import { useMemo, useCallback, useState, useEffect, } from "react";
import { useHistory } from "react-router";
import { Helmet } from "react-helmet";
import { gql, useQuery } from "@apollo/client";

import { useStoreContext } from "../../contexts/StoreProvider";
import Layout from "../../components/Layout";
import Table from "../../components/Table";
import { Heading } from "../../components/Type";
import { ArrowRight } from "../../components/Icons/general-interface-1";
import Button from "../../components/Button";
import StatusFilterDropdown from "../../components/FilterDropdowns/StatusFilterDropdown";
import CepFilterDropdown, { cepList, } from "../../components/FilterDropdowns/CepFilterDropdown";
import SearchInput from "../../components/SearchInput";
import Loader from "../../components/Loader";
import EmptyView from "../../components/Table/EmptyView";
import { ClientCell, DateCell, BooleanCell, ProfileListCell, StatusCell, } from "../../components/Table/TableCells";
import styles from "./index.module.scss";
import { ClientType } from "../../types/clientType";
import { statusList } from "../../utils/status";
import { FilterType } from "../../types/contextType";

const GET_CLIENTS = gql`
  query Clients(
    $limit: Int
    $sort: String
    $start: Int
    $where: JSON
    $connectionWhere: JSON
  ) {
    clients(limit: $limit, sort: $sort, start: $start, where: $where) {
      updated_at
      id
      first_name
      last_name
      health_card
      health_card_version
      cep
      client_files {
        id
        status
        updated_at
      }
      sales_reps {
        id
        first_name
        last_name
        role {
          name
          type
        }
      }
      authorizers {
        id
        first_name
        last_name
        role {
          name
        }
      }
    }
    clientsConnection(where: $connectionWhere) {
      aggregate {
        count
      }
    }
  }
`;

type WhereType = {
  [key: string]: string | { [key: string]: boolean | string }[] | string[];
};

const Dashboard = () => {
  const { state: { user, filter }, dispatch } = useStoreContext();
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState<FilterType>({cep: null, status: null});
  const [, setBoundaryError] = useState<Error | null>(null);
  const [search, setSearch] = useState<string | undefined>();
  const history = useHistory();

  useEffect(() => {
    setFilters(filter);
  }, [filter]);

  const { data, loading, refetch } = useQuery(GET_CLIENTS, {
    fetchPolicy: "network-only",
    onError: (error) => {
      setBoundaryError(() => {
        throw error;
      });
    },
  });

  const clientData: {
    clients: ClientType[];
    count: number;
  } = {
    clients: data?.clients ?? [],
    count: data?.clientsConnection?.aggregate?.count ?? 0,
  };

  const role = user?.role.type;
  const handleFilterChange = (
    id: string,
    filter?: { value: string; label: string }
  ) => {
    const newFilter = { [id]: filter ? filter.value : null };
    setFilters((prev) => ({ ...prev, ...newFilter }));
    dispatch({
      type: "FILTER",
      payload: { ...filters, ...newFilter },
    });
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const fetchData = useCallback(
    ({ pageSize, pageIndex, sortBy }) => {
      const variables: {
        limit: number;
        start: number;
        sort?: unknown;
        where?: WhereType;
        connectionWhere?: WhereType;
      } = {
        limit: pageSize,
        start: pageIndex * pageSize,
      };
      //sort
      if (sortBy.length) {
        if (sortBy[0].id === "status") {
          variables.sort = `client_files.status:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else if (sortBy[0].id === "sales_reps") {
          variables.sort = `sales_reps.first_name:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else if (sortBy[0].id === "authorizers") {
          variables.sort = `authorizers.first_name:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else if (sortBy[0].id === "updated") {
          variables.sort = `client_files.updated_at:${
            sortBy[0].desc ? "DESC" : "ASC"
          }`;
        } else {
          variables.sort = `${sortBy[0].id}:${sortBy[0].desc ? "DESC" : "ASC"}`;
        }
      }

      const where: WhereType = {};
      if (filter.cep || filter.status || search) {
        const and: { [key: string]: boolean | string }[] = [];
        const or: { [key: string]: boolean | string }[] = [];
        if (filter.cep) {
          and.push({ cep: filter.cep === "yes" });
        }
        if (filter.status) {
          and.push({ "client_files.status": filter.status });
        }
        if (search) {
          or.push({ first_name_contains: search });
          or.push({ last_name_contains: search });
        }
        if (and.length) where._and = and;
        if (or.length) where._or = or;
      }
      variables.where = where;

      if (user) {
        const role = (["Authorizer", "Motion Support"].includes(user.role.name)  ? "authorizers" : "sales_reps");
        //TODO: Move to api if possible
        variables.connectionWhere = { [`${role}_in`]: [user.id], ...where };
      }

      setPageSize(pageSize);
      refetch(variables);
    },
    [refetch, user, filter, search]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Name",
        accessor: "first_name",
        Cell: ({ row }) => <ClientCell client={row.original} />,
      },
      {
        Header: "Updated",
        accessor: "updated",
        Cell: ({ row }) => (
          <DateCell
            value={
              row.original?.client_files?.sort(
                (a, b) =>
                  new Date(b.updated_at).getTime() -
                  new Date(a.updated_at).getTime()
              )?.[0]?.updated_at
            }
          />
        ),
      },
      {
        Header: "CEP Client",
        accessor: "cep",
        Cell: ({ value }) => <BooleanCell value={value} />,
      },
      {
        Header: `${(role === "authorizer" || role === "motion_support") ? "Motion Rep(s)" : "Authorizer(s)"}`,
        accessor: (role === "authorizer" || role === "motion_support") ? "sales_reps" : "authorizers",
        Cell: ({ row }) => {
          const cellData = (role === "authorizer" || role === "motion_support") ? row.original.sales_reps : row.original.authorizers;

          return <ProfileListCell data={cellData} />;
        },
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }) => <StatusCell files={row.original.client_files} />,
      },
    ],
    [role]
  );

  const getCurrentFilters = useMemo(() => {
    const currentFilters = Object.keys(filters)
      .filter((f) => !!filters[f])
      .map((filter) => {
        let id;
        if (filter === "cep") {
          id = "cep";
        }
        if (filter === "status") {
          id = "status";
        }
        return { id, value: filters[filter] };
      });

    if (search) {
      currentFilters.push({ id: "first_name", value: search });
    } else {
      currentFilters.filter((f) => f.id !== "first_name");
    }
    return currentFilters;
  }, [filters, search]);

  const pageCount = data ? Math.ceil(data.clientsConnection.aggregate.count / pageSize) : 0;

  const handleResetFilters = () => {
    setFilters({cep: null, status: null});
    dispatch({
      type: "FILTER",
      payload: {cep: null, status: null},
    });

    setSearch("");
    const input = (document.getElementById("search-filter") as HTMLInputElement);
    if (input) input.value = "";
  };

  return (
    <Layout>
      <Helmet>
        <title>Motion Home</title>
      </Helmet>
      <div className={styles.headerRow}>
        <Heading size="h3" component="h1">
          Clients
        </Heading>
        <Button
          primary
          icon={<ArrowRight />}
          onClick={() => history.push("/client/new")}
        >
          Create new client file
        </Button>
      </div>
      {loading && (
        <Loader heading="Loading client list..." className={styles.loader} />
      )}
      <div className={loading ? styles.tableHide : styles.tableShow}>
        <div className={styles.filterRow}>
          <div className={styles.filterItem}>
            <SearchInput
              onChange={handleSearchChange}
              placeholder="Search by name"
              loading={loading}
            />
          </div>
          <div className={styles.filterItem}>
            <StatusFilterDropdown
              onChange={(status: { value: string; label: string }) => handleFilterChange("status", status) }
              value={statusList.find((status) => status.value === filters.status)}
            />
          </div>
          <div className={styles.filterItem}>
            <CepFilterDropdown
              onChange={(enabled: { value: string; label: string }) => handleFilterChange("cep", enabled) }
              value={cepList.find((cep) => cep.value === filters.cep)}
            />
          </div>
          <div>
            <Button
              secondary
              onClick={handleResetFilters}
            >
              Reset Filters
            </Button>
          </div>
        </div>
        <Table
          data={clientData.clients}
          columns={columns}
          pageCount={pageCount}
          fetchData={fetchData}
          total={clientData.count}
          loading={loading}
          resetKey={JSON.stringify({ filters, search })}
          handleRowClick={(row) => {
            history.push(`/client/${row.original.id}`);
          }}
          emptyView={
            <EmptyView
              message={
                search || filters.cep || filters.status
                  ? `No results found matching your search criteria.`
                  : "You do not have any clients on file yet."
              }
            >
              <Button primary onClick={() => history.push("/client/new")}>
                Create new client file
              </Button>
            </EmptyView>
          }
          filters={getCurrentFilters}
        />
      </div>
    </Layout>
  );
};

export default Dashboard;
